import {
	Paper,
	Table,
	TableBody,
	TableCell,
	TableContainer,
	TableHead,
	TablePagination,
	TableRow,
	useMediaQuery,
} from '@mui/material';
import dayjs from 'dayjs';
import { Badge, Button, Checkbox, Label, TextInput, ToggleSwitch } from 'flowbite-react';
import React, { useCallback, useEffect, useState } from 'react';
import { useParams } from 'react-router-dom';
import Select, { SingleValue } from 'react-select';
import { Dropdown } from 'shared-utils';
import { useGetMaxNumberOfRoundsForPosition, useGetUsersListForARound } from 'src/hooks/application.hooks';
import { ApplicationRoundType, BriefDebriefCallDocument, PositionDocument, UIApplication } from 'types';
import RecruiterCandidateUpdateAssignment from './RecruiterCandidateUpdateAssignment';
import { Drawer } from '../shared/Drawer';
import { useMutation, useQueryClient } from 'react-query';
import { bulkAction } from 'src/apis/application';
import { useToast } from 'src/context/ToastProvider';
import { debounce, get } from 'lodash';
import BriefDebriefModal from './BriefDebriefModal';
import DynamicFieldSelection from '../position/DynamicFieldSelection';
import { getMyCompaniesListQuery } from 'src/apis/company';
import { isValidObjectId } from 'src/utils/helper';

const BulkAssignmentChangeModal = ({
	roundType,
	ctaText,
	onSubmit,
	drawerType,
	selectedPosition: defaultSelectedPosition,
	scheduleType,
	rescheduleCallId,
	defaultSelectedUserIds,
	callDetails,
}: {
	roundType?: ApplicationRoundType;
	ctaText?: string;
	onSubmit?: () => void;
	drawerType?: string;
	selectedPosition?: string;
	scheduleType?: 'request' | 'schedule';
	rescheduleCallId?: string;
	defaultSelectedUserIds?: string[];
	callDetails?: BriefDebriefCallDocument;
}) => {
	const toast = useToast();
	const { positionId } = useParams();
	const queryClient = useQueryClient();
	const isMobile = useMediaQuery('(max-width:960px)');
	const [round, setRound] = useState<number>(1);
	const [page, setPage] = useState(0);
	const [filteredUsers, setFilteredUsers] = useState<UIApplication[]>();
	const [rowsPerPage, setRowsPerPage] = useState(10);
	const [searchInput, setSearchInput] = useState<string>();
	const [bulkSelectedCandidates, setBulkSelectedCandidates] = useState<UIApplication[]>([]);
	const [roundOptions, setRoundOptions] = useState<Dropdown[]>([]);
	const [openUpdateAssignmentDrawer, setOpenUpdateAssignmentDrawer] = useState(false);
	const [companyOptions, setCompanyOptions] = useState<{ name: string; value: string }[]>([]);
	const [selectedCompany, setSelectedCompany] = useState<string>();
	const [selectedPosition, setSelectedPosition] = useState<string | undefined>(defaultSelectedPosition);
	const [positionOptions, setPositionOptions] = useState<Dropdown[]>([]);
	const [showActiveOnly, setShowActiveOnly] = useState(true);
	const [companyPositions, setSelectedCompanyPosiitons] = useState<Record<string, PositionDocument[]>>({});

	const { data: userData, refetch: refetchUsersList } = useGetUsersListForARound(
		positionId || selectedPosition || '',
		round || 0,
		roundType,
		() => {},
		searchInput,
		showActiveOnly, // for active applicants
	);

	const { refetch } = useGetMaxNumberOfRoundsForPosition(
		positionId || defaultSelectedPosition || selectedPosition || '',
		d => {
			setRoundOptions(
				Array.from({ length: d?.maxNumberOfRounds || 0 }, (_, i) => ({
					label: `Round ${i + 1}`,
					value: (i + 1).toString(),
				})),
			);
		},
	);

	const tableColumns = [
		{ label: 'Candidate', align: 'left', minWidth: 'fit-content' },
		{ label: 'Email', align: 'center', minWidth: 'fit-content' },
		{ label: 'Phone', align: 'left', minWidth: 'fit-content' },
		{ label: 'Round No.', align: 'left', minWidth: 'fit-content' },
		{ label: 'JobSeeker Deadline', align: 'left', minWidth: 'fit-content' },
		{ label: 'Assignment Deadline', align: 'left', minWidth: 'fit-content' },
	];

	const handleChangePage = (event: unknown, newPage: number) => {
		setPage(newPage);
	};

	const handleChangeRowsPerPage = (event: React.ChangeEvent<HTMLInputElement>) => {
		setRowsPerPage(parseInt(event.target.value, 10));
		setPage(0);
	};

	const fetchCompanyNameFn = useMutation(['getMyCompaniesListQuery'], getMyCompaniesListQuery, {
		onSuccess(data) {
			setCompanyOptions(data.companies?.map(c => ({ name: c.name, value: c._id })) ?? []);
			setSelectedCompanyPosiitons(
				data.companies?.reduce(
					(acc, company) => {
						acc[company._id] = company.positions ?? [];
						return acc;
					},
					{} as Record<string, PositionDocument[]>,
				) ?? {},
			);
		},
	});

	const bulkActionFn = useMutation(['bulkAction'], bulkAction, {
		onSuccess: async (data: any) => {
			queryClient.invalidateQueries(['getRecommendationsForPosition', positionId || selectedPosition]);
			queryClient.refetchQueries(['getRecommendationsForPosition', positionId || selectedPosition]);
			refetchUsersList();
			toast({
				title:
					drawerType === 'schedule-brief-debrief-call' ? 'Brief/Debrief Call Scheduled' : 'Assignment Deadline Updated',
				type: 'success',
			});

			setOpenUpdateAssignmentDrawer(false);
		},
		onError(error: Error) {
			const errorMessage = get(error, ['response', 'data', 'message'], error.message);
			const match = errorMessage.match(/InvalidUserError: (.*)/);
			if (match) {
				toast({
					title: match[1],
					type: 'error',
				});
			} else {
				toast({
					title: errorMessage,
					type: 'error',
				});
			}
		},
	});

	useEffect(() => {
		console.log({ selectedCompany });
		if (selectedCompany)
			setPositionOptions(
				companyPositions[selectedCompany]?.map(position => ({
					label: position.title,
					value: position._id,
				})),
			);
		else setPositionOptions([]);
	}, [selectedCompany]);

	useEffect(() => {
		if (defaultSelectedPosition) setSelectedPosition(defaultSelectedPosition);
	}, [defaultSelectedPosition]);

	useEffect(() => {
		if (defaultSelectedUserIds?.length && userData?.applications?.length) {
			const filteredApplications =
				userData?.applications?.filter(app => defaultSelectedUserIds?.includes(String(app.user._id))) ?? [];
			setBulkSelectedCandidates(filteredApplications);
		}
		setFilteredUsers(userData?.applications);
	}, [defaultSelectedUserIds, userData?.applications]);

	useEffect(() => {
		if (selectedPosition) {
			refetch();
		}
	}, [selectedPosition]);

	const changeHandler = (e: { target: { value: React.SetStateAction<string | undefined> } }) => {
		setSearchInput(e.target.value);
	};

	const debouncedChangeHandler = useCallback(debounce(changeHandler, 1000), [searchInput]);

	return (
		<div className="pb-2">
			{!positionId && !defaultSelectedPosition && drawerType === 'schedule-brief-debrief-call' ? (
				<div className="flex gap-4 flex-wrap w-full">
					<div className="mb-2 block tmd:flex-1 w-full">
						<div className="mb-[5px]">
							<Label value="Company" />
							<span className="text-[red]">*</span>
						</div>
						<div>
							<DynamicFieldSelection
								onChange={e => {
									if (isValidObjectId(e)) setSelectedCompany(e);
								}}
								onType={e => fetchCompanyNameFn.mutate(e)}
								options={companyOptions}
								placeholder="Select a Company (Type to search)"
								creatable={true}
							/>
						</div>
					</div>
					<div className="mb-2 block tmd:flex-1 w-full whiteBgSelect">
						<div className="mb-[5px]">
							<Label value="Position" />
							<span className="text-[red]">*</span>
						</div>
						<div>
							<Select
								options={positionOptions}
								menuPlacement={'bottom'}
								classNamePrefix="my-select-plans"
								className="single-value-select-tag"
								onChange={(val: SingleValue<Dropdown>) => {
									setSelectedPosition(val?.value);
								}}
								placeholder={'Select Position'}
							/>
						</div>
					</div>
				</div>
			) : null}

			<div className="flex items-center flex-wrap gap-4">
				<div className="w-full pt-3 tmd:flex-1 whiteBgSelect">
					<div className="mb-[5px]">
						<Label>{'Select Round'}</Label>
						<span className="text-[red]">*</span>
					</div>

					<Select
						isClearable
						options={roundOptions}
						menuPlacement={'bottom'}
						classNamePrefix="my-select-plans"
						className="single-value-select-tag"
						onChange={(val: SingleValue<Dropdown>) => {
							setBulkSelectedCandidates([]);
							setRound(Number(val?.value || 0));
						}}
						placeholder={'Select Round'}
						value={roundOptions.find(option => option.value === round.toString())}
					/>
				</div>
				<div className="w-full pt-3 tmd:flex-1 whiteBgSelect">
					<div className="mb-[5px]">
						<Label>{'Search Name'}</Label>
						<span className="text-[red]">*</span>
					</div>

					<TextInput
						color="purplePlaced"
						defaultValue={searchInput}
						placeholder="Search name,email,phone,student code"
						onChange={e => {
							setBulkSelectedCandidates([]);
							debouncedChangeHandler(e);
						}}
					/>
				</div>
			</div>

			<div className="flex items-center flex-wrap gap-4">
				<div className="pt-3 flex items-center gap-2">
					<Label htmlFor="showActiveOnly">Show Active Only</Label>
					<ToggleSwitch name="showActiveOnly" checked={showActiveOnly} onChange={e => setShowActiveOnly(e)} />
				</div>
			</div>

			<div className="">
				<Paper
					sx={{
						mt: '24px',
						width: '100%',
						overflow: 'hidden',
						bg: 'white',
						borderRadius: '8px',
						boxShadow: '0px 1px 2px -1px rgba(0, 0, 0, 0.10), 0px 1px 3px 0px rgba(0, 0, 0, 0.10)',
						isolation: 'isolate',
					}}
				>
					<TableContainer sx={{ maxHeight: 440 }}>
						<Table aria-label="sticky table">
							<TableHead sx={{ position: 'sticky', top: 0, zIndex: 1 }}>
								<TableRow
									sx={{
										'&': {
											background: '#F2F2F5',
										},
									}}
								>
									<TableCell align="center" padding="checkbox" sx={{ paddingLeft: '15px' }}>
										<Checkbox
											color="purplePlaced"
											onChange={(event: React.ChangeEvent<HTMLInputElement>) => {
												if (event.target.checked) {
													setBulkSelectedCandidates(userData?.applications || []);
													return;
												}
												setBulkSelectedCandidates([]);
											}}
										/>
									</TableCell>
									{tableColumns.map((column, id) => (
										<TableCell
											className="text-[#1F3343] font-inter !font-bold text-sm whitespace-pre"
											key={id}
											style={{ minWidth: column.minWidth }}
										>
											{column.label}
										</TableCell>
									))}
								</TableRow>
							</TableHead>
							<TableBody className="!relative">
								{filteredUsers && filteredUsers?.length > 0 ? (
									filteredUsers
										?.slice(page * rowsPerPage, page * rowsPerPage + rowsPerPage)
										.map((candidate: UIApplication, id: number) => {
											const isSelected = bulkSelectedCandidates.find(
												bulkCandidate => bulkCandidate?.user?.handler === candidate?.user?.handler,
											)
												? true
												: false;

											return (
												<TableRow
													aria-checked={isSelected}
													hover
													role="checkbox"
													tabIndex={-1}
													key={candidate._id}
													onClick={() => {
														if (isSelected)
															setBulkSelectedCandidates(prev =>
																prev.filter(people => people?.user?.handler !== candidate?.user?.handler),
															);
														else setBulkSelectedCandidates(prev => [...prev, candidate]);
													}}
												>
													<TableCell align="center" padding="checkbox" sx={{ paddingLeft: '15px' }}>
														<Checkbox color="purplePlaced" checked={isSelected} />
													</TableCell>

													<TableCell className="whitespace-pre">{candidate?.user?.name}</TableCell>

													<TableCell>{candidate?.user?.email}</TableCell>

													<TableCell>{candidate?.user?.phone}</TableCell>

													<TableCell className="!text-center">{candidate?.ongoingRound?.order}</TableCell>

													<TableCell className="whitespace-pre">
														{dayjs(candidate?.ongoingRound?.jobSeekerAssignmentExpiry).format('DD MMMM YYYY HH:mm a')}
													</TableCell>

													<TableCell className="whitespace-pre">
														{dayjs(candidate?.ongoingRound?.expiry).format('DD MMMM YYYY HH:mm a')}
													</TableCell>
												</TableRow>
											);
										})
								) : (
									<div>
										<div className="invisible">No User Found</div>
										<Badge color="red" className="absolute inset-0 inline-table m-auto rounded-[8px] [&_span]:p-2">
											No User Found
										</Badge>
									</div>
								)}
							</TableBody>
						</Table>
					</TableContainer>
					<TablePagination
						sx={{
							'&': {
								background: '#FAFAFC',
							},
						}}
						rowsPerPageOptions={[10, 25, 100]}
						component="div"
						count={userData?.applications?.length || 0}
						rowsPerPage={rowsPerPage}
						page={page}
						onPageChange={handleChangePage}
						onRowsPerPageChange={handleChangeRowsPerPage}
					/>
				</Paper>
			</div>

			<div className="flex justify-end w-full pt-4">
				<Button
					className="w-full md:w-fit"
					color="purplePlaced"
					disabled={!bulkSelectedCandidates?.length || (drawerType === 'schedule-brief-debrief-call' && !round)}
					onClick={() => {
						if (bulkSelectedCandidates?.length) {
							if (onSubmit) {
								onSubmit();
							} else {
								setOpenUpdateAssignmentDrawer(true);
							}
						}
					}}
				>
					{ctaText || 'Update Deadline'}
				</Button>
			</div>

			<Drawer
				minHeight="max-content"
				maxHeight="90dvh"
				isModal={!isMobile}
				boxShadow="0px -8px 32px 0px #00000040"
				isOpen={openUpdateAssignmentDrawer}
				onClose={() => {
					setOpenUpdateAssignmentDrawer(false);
				}}
				header={
					<h1 className="text-[#1C1C28] dark:text-inherit text-lg font-inter font-[500] px-5 pt-4">
						{ctaText || 'Update Deadline'}
					</h1>
				}
				body={
					<div className="px-3 py-4">
						{drawerType === 'schedule-brief-debrief-call' ? (
							<BriefDebriefModal
								roundNumber={round}
								callDetails={callDetails}
								userName="Candidates"
								positionId={positionId || selectedPosition || ''}
								isLoading={bulkActionFn.isLoading}
								defaultScheduleType={scheduleType}
								onSubmit={data => {
									bulkActionFn.mutate({
										rescheduleCallId,
										action: 'schedule-brief-debrief-call',
										positionId: positionId || selectedPosition || '',
										userIds: bulkSelectedCandidates.map(candidate => candidate?.user?._id),
										message: data?.message,
										callType: data?.callType,
										meetingDateTime: data?.meetingDateTime,
										meetingLink: data?.meetingLink,
										duration: data?.duration,
										additionalEmails: data?.additionalEmails,
										roundNumber: data?.roundNumber,
										roundType: data?.roundType,
										prepCoach: data?.prepCoach,
										label: data?.label,
									});
								}}
							/>
						) : (
							<RecruiterCandidateUpdateAssignment
								userName="Candidates"
								positionId={positionId}
								onSubmit={({ assignmentDeadline, jobSeekerAssignmentDeadline }) => {
									bulkActionFn.mutate({
										positionId: positionId || '',
										action: 'update-assignment-deadline',
										userIds: bulkSelectedCandidates?.map(candidate => candidate?.user?._id),
										assignmentDeadline: assignmentDeadline,
										jobSeekerAssignmentDeadline: jobSeekerAssignmentDeadline,
									});
								}}
							/>
						)}
					</div>
				}
			/>
		</div>
	);
};

export default BulkAssignmentChangeModal;
